import React, { useEffect, useState } from "react";
import { fetchMaintenance, resolveMaintenance } from "../services/api";
import { parseServerDate } from "../utils/time";
import { useT } from "../i18n/useT";

// Front Desk — Maintenance tickets. A lightweight room-repair log: a ticket is
// raised when a room is set out-of-order with the "maintenance" category (or
// logged manually); marking it done records the completion time and returns the
// room to service. A full maintenance department is a later V2 item.
const S = {
  en: {
    title: "Maintenance", subtitle: "Room repairs — raised when a room goes out of order, cleared when the work is done",
    all: "All", open: "Open", done: "Done",
    none: "No maintenance tickets.", err: "Could not load tickets.",
    col_room: "Room", col_reason: "Reason", col_category: "Category", col_reported: "Reported", col_status: "Status",
    mark_done: "Mark done", done_notes_label: "Resolution notes (optional)", cancel: "Cancel", save_err: "Could not save.",
    cat_maintenance: "Maintenance", cat_cleaning: "Cleaning", cat_other: "Other",
  },
  am: {
    title: "ጥገና", subtitle: "የክፍል ጥገና — ክፍል ከአገልግሎት ውጪ ሲሆን ይነሳል፣ ሥራው ሲጠናቀቅ ይዘጋል", // TODO verify am
    all: "ሁሉም", open: "ክፍት", done: "ተጠናቋል", // TODO verify am
    none: "የጥገና ትኬቶች የሉም።", err: "ትኬቶችን መጫን አልተቻለም።", // TODO verify am
    col_room: "ክፍል", col_reason: "ምክንያት", col_category: "ምድብ", col_reported: "ተዘግቧል", col_status: "ሁኔታ", // TODO verify am
    mark_done: "እንደተጠናቀቀ ምልክት", done_notes_label: "የመፍትሔ ማስታወሻ (አማራጭ)", cancel: "ተወው", save_err: "ማስቀመጥ አልተሳካም።", // TODO verify am
    cat_maintenance: "ጥገና", cat_cleaning: "ጽዳት", cat_other: "ሌላ", // TODO verify am
  },
  om: {
    title: "Suphaa", subtitle: "Suphaa kutaa — yeroo kutaan tajaajila ala ta'u ka'a, hojiin xumuramnaan cufama", // TODO verify om
    all: "Hunda", open: "Banaa", done: "Xumurame", // TODO verify om
    none: "Tikeetii suphaa hin jiru.", err: "Tikeetii fe'uun hin dandeenye.", // TODO verify om
    col_room: "Kutaa", col_reason: "Sababa", col_category: "Ramaddii", col_reported: "Gabaafame", col_status: "Haala", // TODO verify om
    mark_done: "Akka xumurametti mallatteessi", done_notes_label: "Yaadannoo furmaataa (filannoo)", cancel: "Dhiisi", save_err: "Kaa'uun hin milkoofne.", // TODO verify om
    cat_maintenance: "Suphaa", cat_cleaning: "Qulqullina", cat_other: "Kan biraa", // TODO verify om
  },
};

const STATUSES = ["open", "done"];
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const ST_TINT = { open: "#FBF0E4", done: "#EAF3DE" };
const ST_FG = { open: "#8A4B0C", done: "#27500A" };
const CAT_TINT = { maintenance: "#EEF2FB", cleaning: "#EAF3DE", other: "#EFEFF2" };
const CAT_FG = { maintenance: "#0C447C", cleaning: "#27500A", other: "#5b6472" };

export default function MaintenancePage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState("all");
  const [doneId, setDoneId] = useState(null);
  const [doneNotes, setDoneNotes] = useState("");

  const load = () => {
    setErr(false);
    setRows(null);
    fetchMaintenance({ status: filter === "all" ? undefined : filter })
      .then(setRows)
      .catch(() => { setRows([]); setErr(true); });
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtTime = (v) => {
    const d = parseServerDate(v);
    return d ? d.toLocaleString() : "—";
  };

  const catLabel = (c) => (c ? t("cat_" + c) : "—");

  const openDone = (x) => { setDoneId(x.id); setDoneNotes(""); };
  const cancelDone = () => { setDoneId(null); setDoneNotes(""); };
  const confirmDone = (x) => {
    // Resolution notes are OPTIONAL — a done with no note is fine.
    resolveMaintenance(x.id, { notes: doneNotes.trim() || undefined })
      .then(() => { cancelDone(); load(); })
      .catch(() => window.alert(t("save_err")));
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, alignItems: "center" }}>
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={filter === s ? "btn primary small" : "btn secondary small"}>{t(s)}</button>
        ))}
      </div>

      {rows == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : err ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("err")}</div>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((x) => (
            <div key={x.id} style={{ ...CARD, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 90 }}>
                <div className="muted" style={{ fontSize: 11 }}>{t("col_room")}</div>
                <div style={{ fontWeight: 700 }}>{x.room_name || "—"}</div>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div className="muted" style={{ fontSize: 11 }}>{t("col_reason")}</div>
                <div style={{ fontSize: 13 }}>{x.reason || "—"}</div>
                {x.status === "done" && x.resolution_notes && (
                  <div className="muted" style={{ fontSize: 12 }}>{x.resolution_notes}</div>
                )}
              </div>
              <span style={{ background: CAT_TINT[x.category] || "#eef2f2", color: CAT_FG[x.category] || "#5b6472", borderRadius: 16, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{catLabel(x.category)}</span>
              <div style={{ minWidth: 150 }}>
                <div className="muted" style={{ fontSize: 11 }}>{t("col_reported")}</div>
                <div style={{ fontSize: 12.5 }}>{fmtTime(x.reported_at)}</div>
              </div>
              <span style={{ background: ST_TINT[x.status] || "#eef2f2", color: ST_FG[x.status] || "#5b6472", borderRadius: 16, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{t(x.status)}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {x.status === "open" && doneId !== x.id && (
                  <button className="btn secondary small" onClick={() => openDone(x)}>{t("mark_done")}</button>
                )}
              </div>
              {doneId === x.id && (
                <div style={{ flexBasis: "100%", marginTop: 8, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                  <label style={{ fontSize: 12, color: "#5f6b78", display: "block" }}>{t("done_notes_label")}
                    <textarea rows={2} value={doneNotes} autoFocus
                      onChange={(e) => setDoneNotes(e.target.value)}
                      style={{ width: "100%", maxWidth: 480, marginTop: 4, padding: 8, borderRadius: 8, border: "1px solid #d8dee2" }} />
                  </label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button className="btn primary small" onClick={() => confirmDone(x)}>{t("mark_done")}</button>
                    <button className="btn secondary small" onClick={cancelDone}>{t("cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
